import { useOutsideClick } from '@helpers/hooks/use-outside-click/useOutsideClick';
import {
  type AnimationEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const THINKING_FEEDBACK_DISMISS_DELAY_MS = 3_000;

const DISMISS_ANIMATION_SAFETY_MS = 650;

type DismissalVariant = 'fly' | 'fallback';
type DismissalStatus = 'active' | 'dismissing' | 'completed';

interface UseAlfredThinkingFeedbackDismissalOptions {
  conversationId: string;
  onNavigate: (conversationId: string) => void;
  onDismiss: () => void;
}

export const useAlfredThinkingFeedbackDismissal = ({
  conversationId,
  onNavigate,
  onDismiss,
}: UseAlfredThinkingFeedbackDismissalOptions) => {
  const [dismissalVariant, setDismissalVariant] = useState<DismissalVariant>();
  const rootRef = useRef<HTMLDivElement>(null);
  const autoDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const animationSafetyTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const activeConversationIdRef = useRef(conversationId);
  const dismissalStatusRef = useRef<DismissalStatus>('active');
  const onDismissRef = useRef(onDismiss);

  onDismissRef.current = onDismiss;

  const clearTimers = useCallback(() => {
    if (autoDismissTimeoutRef.current) {
      clearTimeout(autoDismissTimeoutRef.current);
      autoDismissTimeoutRef.current = null;
    }

    if (animationSafetyTimeoutRef.current) {
      clearTimeout(animationSafetyTimeoutRef.current);
      animationSafetyTimeoutRef.current = null;
    }
  }, []);

  const completeDismissal = useCallback(() => {
    if (dismissalStatusRef.current === 'completed') {
      return;
    }

    dismissalStatusRef.current = 'completed';
    clearTimers();
    onDismissRef.current();
  }, [clearTimers]);

  // useOutsideClick re-registers its document listener when this callback changes.
  const beginDismissal = useCallback(() => {
    if (dismissalStatusRef.current !== 'active') {
      return;
    }

    dismissalStatusRef.current = 'dismissing';
    clearTimers();

    const root = rootRef.current;
    const target = document.querySelector<HTMLElement>(
      '[data-alfred-nav-animation-target]'
    );
    const rootRect = root?.getBoundingClientRect();
    const targetRect = target?.getBoundingClientRect();
    const hasValidGeometry =
      !!rootRect?.width &&
      !!rootRect.height &&
      !!targetRect?.width &&
      !!targetRect.height;

    if (root && rootRect && targetRect && hasValidGeometry) {
      const translateX =
        targetRect.left +
        targetRect.width / 2 -
        (rootRect.left + rootRect.width / 2);
      const translateY =
        targetRect.top +
        targetRect.height / 2 -
        (rootRect.top + rootRect.height / 2);
      const scale = Math.min(
        targetRect.width / rootRect.width,
        targetRect.height / rootRect.height
      );

      root.style.setProperty('--dismiss-translate-x', `${translateX}px`);
      root.style.setProperty('--dismiss-translate-y', `${translateY}px`);
      root.style.setProperty('--dismiss-scale', `${scale}`);
      setDismissalVariant('fly');
    } else {
      setDismissalVariant('fallback');
    }

    animationSafetyTimeoutRef.current = setTimeout(
      completeDismissal,
      DISMISS_ANIMATION_SAFETY_MS
    );
  }, [clearTimers, completeDismissal]);

  useOutsideClick({
    ref: rootRef,
    callback: beginDismissal,
    eventType: 'click',
  });

  useEffect(() => {
    const root = rootRef.current;

    activeConversationIdRef.current = conversationId;
    root?.style.removeProperty('--dismiss-translate-x');
    root?.style.removeProperty('--dismiss-translate-y');
    root?.style.removeProperty('--dismiss-scale');
    setDismissalVariant(undefined);
    dismissalStatusRef.current = 'active';

    autoDismissTimeoutRef.current = setTimeout(() => {
      if (activeConversationIdRef.current === conversationId) {
        beginDismissal();
      }
    }, THINKING_FEEDBACK_DISMISS_DELAY_MS);

    return () => {
      clearTimers();
    };
  }, [beginDismissal, clearTimers, conversationId]);

  const handleNavigation = () => {
    if (dismissalStatusRef.current !== 'active') {
      return;
    }

    dismissalStatusRef.current = 'completed';
    clearTimers();
    onNavigate(conversationId);
    onDismissRef.current();
  };

  const handleAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (
      event.currentTarget === event.target &&
      dismissalStatusRef.current === 'dismissing'
    ) {
      completeDismissal();
    }
  };

  return {
    dismissalVariant,
    handleAnimationEnd,
    handleNavigation,
    rootRef,
  };
};
